import { useState, useEffect, useCallback, useRef } from 'react';

const TICK_MS = 60 * 1000;

function readStoredCooldown(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function computeState(stored, durationMs) {
  if (!stored) {
    return { active: false, msRemaining: 0, lastReadingCellIndex: null };
  }
  const elapsed = Date.now() - stored.timestamp;
  const msRemaining = Math.max(0, durationMs - elapsed);
  return {
    active: msRemaining > 0,
    msRemaining,
    lastReadingCellIndex:
      typeof stored.cellIndex === 'number' ? stored.cellIndex : null,
  };
}

export default function useCooldown({ storageKey, durationMs }) {
  const [state, setState] = useState(() =>
    computeState(readStoredCooldown(storageKey), durationMs)
  );
  const intervalRef = useRef(null);

  const refreshFromStorage = useCallback(() => {
    setState(computeState(readStoredCooldown(storageKey), durationMs));
  }, [storageKey, durationMs]);

  useEffect(() => {
    if (!state.active) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    if (!intervalRef.current) {
      intervalRef.current = setInterval(refreshFromStorage, TICK_MS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [state.active, refreshFromStorage]);

  const startCooldown = useCallback((cellIndex) => {
    const payload = {
      timestamp: Date.now(),
      cellIndex: typeof cellIndex === 'number' ? cellIndex : null,
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // Storage full — cooldown degrades to in-memory only
    }
    setState({
      active: true,
      msRemaining: durationMs,
      lastReadingCellIndex: payload.cellIndex,
    });
  }, [storageKey, durationMs]);

  return {
    active: state.active,
    msRemaining: state.msRemaining,
    lastReadingCellIndex: state.lastReadingCellIndex,
    startCooldown,
  };
}
